import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { SkeletonText } from './Skeleton';

/**
 * MANCHETE — o número do dia dito em frase (G1, 07/10/2026).
 *
 * "Hoje entraram **147** pessoas e **108** ficaram (73%). Ontem, nesse mesmo horário: 121."
 * Quem abre o painel 15 vezes por dia lê uma frase mais rápido que quatro tiles. A frase é
 * montada por quem chama, com números que chegaram prontos do servidor; os números vão em
 * `<HeadlineNumber>` (tabular, em destaque), e o `delta` fica no fim da frase.
 *
 * No máximo duas linhas: o que não couber é detalhe, e detalhe mora embaixo.
 */
export interface HeadlineProps extends Omit<HTMLAttributes<HTMLParagraphElement>, 'children'> {
  children?: ReactNode;
  /** `DeltaIndicator` ou outra marca no fim da frase. */
  delta?: ReactNode;
  /** `brand` = dentro do herói violeta. */
  tone?: 'default' | 'brand';
  size?: 'md' | 'lg';
  /** Skeleton de 2 linhas no formato da frase. */
  loading?: boolean;
}

export function Headline({
  children,
  delta,
  tone = 'default',
  size = 'lg',
  loading = false,
  className,
  ...props
}: HeadlineProps) {
  if (loading) {
    return (
      <div role="status" aria-busy="true" className={cn('w-full max-w-xl', className)}>
        <span className="sr-only">Carregando o resumo</span>
        <SkeletonText lines={2} />
      </div>
    );
  }
  return (
    <p
      className={cn(
        'text-pretty font-semibold leading-snug',
        size === 'lg' ? 'text-xl md:text-2xl' : 'text-md',
        tone === 'brand' ? 'text-primary-foreground' : 'text-foreground',
        className,
      )}
      {...props}
    >
      {children}
      {delta ? <span className="ml-2 inline-flex align-middle">{delta}</span> : null}
    </p>
  );
}

/** Um número dentro da manchete: algarismos tabulares e peso maior. */
export function HeadlineNumber({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <strong className={cn('nums font-bold', className)}>{children}</strong>;
}
